import { TaxOfficePicker } from "@/components/TaxOfficePicker";
import { useEffect, useState, useMemo, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Integrations from "@/pages/Integrations";
import Esl from "@/pages/Esl";
import DeviceSettings from "@/pages/DeviceSettings";
import SystemMonitor from "@/pages/SystemMonitor";
import SystemReset from "@/components/SystemReset";
import SectorsSettings from "@/components/SectorsSettings";
import BusinessFeaturesSettings from "@/components/BusinessFeaturesSettings";
import DashboardDesign from "@/components/DashboardDesign";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Settings2, ScanLine, MessageSquare, Save, Plus, Trash2, Loader2, CheckCircle2, AlertTriangle, Send, Users, Plug, Coins, Tag, Palette, Image as ImageIcon, Fuel, ShieldAlert, Boxes, Package, Cpu, Activity, Bell, Mail, Building2, KeyRound, Receipt, LogIn, LayoutDashboard, CalendarClock, Upload, Search, Type, ChevronRight, CornerDownRight, SlidersHorizontal, Layers, ServerCog, Globe,
} from "lucide-react";
import { LOGIN_THEMES, LOGIN_LAYOUTS, LOGIN_TEXT_DEFAULTS, DOC_CONTENT_DEFAULTS, docBrandName, setCompanyProfile, applyGlobalFont, UI_FONTS, UI_FONT_WEIGHTS } from "@/lib/company";
import { FirmLogo } from "@/components/BrandLogo";
import { fileUrl } from "@/components/EntityAvatar";
import DocContentEditor from "@/components/DocContentEditor";
import ProductMigrationCard from "@/components/ProductMigrationCard";

const BASE_TABS = [];

// Ayar Arama indeksi — anahtar kelime → ilgili sekme/alt-sekmeye zıplatır (parça-parça aramayı bitirir).
const SETTINGS_INDEX = [
  { label: "Kaşe & İmza", hint: "Belge kaşe / mühür / imza görseli", kw: "kaşe kase mühür muhur imza stamp damga", tab: "design", designSub: "belge" },
  { label: "Belge / Fiş Marka Adı", hint: "Belgelerde görünen marka adı & sadece logo seçeneği", kw: "marka brand ad belge fiş logo yazı gizle", tab: "design", designSub: "belge" },
  { label: "Firma Logosu & Renkler", hint: "Logo yükle, giriş ekranında göster, marka renkleri (beyaz-etiket)", kw: "logo marka renk primary accent amblem firma beyaz etiket", tab: "firma" },
  { label: "Belge Tasarımı", hint: "Fiş / fatura / irsaliye / makbuz tema & içerik", kw: "belge fatura fiş fis irsaliye makbuz şablon sablon tema qr içerik dekont", tab: "design", designSub: "belge" },
  { label: "Panel Tasarımı", hint: "Uygulama arayüz teması", kw: "panel tasarım tema arayüz görünüm", tab: "design", designSub: "paneldesign" },
  { label: "Yazı Tipi", hint: "Global font tipi / boyut / kalınlık", kw: "font yazı tipi boyut kalınlık büyüklük punto typography", tab: "design", designSub: "font" },
  { label: "Giriş Ekranı", hint: "Login teması ve metinleri", kw: "giriş login şifre ekran karşılama", tab: "design", designSub: "logintheme" },
  { label: "SMS Sağlayıcısı", hint: "SMS platformu / kod gönderimi (NetGSM, İleti Merkezi, Verimor, Twilio)", kw: "sms netgsm iletimerkezi verimor twilio mesaj kod platform sağlayıcı", tab: "comms", commsSub: "sms" },
  { label: "Bildirimler", hint: "E-posta / SMS bildirim anahtarları", kw: "bildirim e-posta email mail uyarı notification", tab: "comms", commsSub: "notifications" },
  { label: "Toptan Yetki", hint: "Toptan fiyat yetkisi", kw: "toptan yetki fiyat wholesale", tab: "comms", commsSub: "wholesale" },
  { label: "Haftalık Özet", hint: "Haftalık rapor e-postası", kw: "haftalık özet rapor weekly", tab: "comms", commsSub: "weekly" },
  { label: "Günlük Risk E-postası", hint: "Kasa açığı / eksik teslim / yetkisiz araç özetini amirlere e-posta", kw: "günlük risk e-posta kasa açık eksik teslim yetkisiz araç radar amir alıcı", tab: "comms", commsSub: "risk" },
  { label: "Firma Bilgileri", hint: "Vergi, adres, ünvan", kw: "firma vergi adres ünvan iletişim telefon", tab: "firma" },
  { label: "Sektörler", hint: "Sektör oluştur / aç-kapa / modül ata", kw: "sektör sector paket eczane market", tab: "sectors", sectorsSub: "sectors" },
  { label: "Modüller & İş Akışı", hint: "Eklenti modüller, yetenekler, zorunlu alanlar, iş akışı ve sektör paketleri", kw: "modül modul iş akışı is akisi workflow özellik ozellik feature paket eklenti onay flag anahtar", tab: "sectors", sectorsSub: "modules" },
  { label: "Kur & Yakıt", hint: "Döviz ve yakıt fiyatları (Sistem Yönetimi)", kw: "kur döviz yakıt fuel fx dolar euro benzin motorin", tab: "monitor", monitorSub: "market" },
  { label: "Entegrasyonlar", hint: "E-Belge / e-Fatura", kw: "entegrasyon e-belge efatura fatura uyumsoft edm izibiz integration", tab: "integrations" },
  { label: "Cihazlar & Yazıcılar", hint: "Yazıcı ve barkod cihazları", kw: "cihaz yazıcı printer barkod terminal", tab: "devices", devicesSub: "devices" },
  { label: "Raf Etiketleri (ESL)", hint: "Elektronik raf etiketi", kw: "esl raf etiket elektronik fiyat", tab: "devices", devicesSub: "esl" },
  { label: "Sistem İzleme", hint: "Loglar ve maliyet", kw: "izleme monitor log kayıt maliyet sistem", tab: "monitor", monitorSub: "security" },
  { label: "Sistem Sıfırlama", hint: "Yedek / demo / temizle", kw: "sıfırla reset yedek demo temizle", tab: "reset" },
  { label: "Güvenlik & Oturum Kilidi", hint: "Giriş uyarıları / şüpheli giriş (ayrı sayfa)", kw: "güvenlik oturum kilit şifre uyarı imkansız seyahat cihaz", to: "/guvenlik" },
  // --- Taşınan ayar uçları (routes/settings.py + POS/platform bölmesi) — tek tıkla ilgili yere zıplar ---
  { label: "Bildirim Kırılımı (Matris)", hint: "Hangi olay hangi role e-posta/SMS gider", kw: "bildirim kırılım matris matrix olay rol event e-posta sms dağıtım", tab: "comms", commsSub: "notifications" },
  { label: "Zorunlu 2FA / Güvenlik Politikası", hint: "Seçili rollerde iki adımlı doğrulama zorunluluğu", kw: "2fa iki adımlı doğrulama güvenlik politika zorunlu totp mfa rol", tab: "comms", commsSub: "notifications" },
  { label: "Standart Ürün Kilidi (Katalog Politikası)", hint: "Standart ürünlerin düzenlenmesini kilitle", kw: "katalog politika standart ürün kilit lock düzenleme", tab: "reset" },
  { label: "Terazi Barkodu", hint: "Tartılı ürün barkodu ön ek / format ayarı", kw: "terazi barkod tartı ağırlık kod format ean prefix", tab: "devices", devicesSub: "devices" },
  { label: "İskonto Politikası", hint: "Yüksek iskonto eşiği ve onay (Onaylar sayfası)", kw: "iskonto indirim discount politika eşik onay yüksek", to: "/onaylar" },
  { label: "İskonto Yetkileri & Standartları", hint: "Rol / kullanıcı iskonto hakları (Roller sayfası)", kw: "iskonto indirim yetki rol kullanıcı standart hak kota grant", to: "/roller" },
  { label: "Az-Stok Eşiği", hint: "Kritik stok uyarı eşiği (Stok sayfası)", kw: "az stok düşük kritik eşik uyarı minimum low stock threshold", to: "/stok" },
  { label: "Kasa Fark Eşiği", hint: "Kasa açık/fazla uyarı eşiği (Vardiyalar)", kw: "kasa fark açık fazla eşik vardiya sayım over short cash", to: "/vardiyalar" },
  { label: "Vade Hatırlatma Günü", hint: "Çek/senet vade hatırlatması (Muhasebe)", kw: "vade hatırlatma çek senet gün ödeme muhasebe due reminder", to: "/muhasebe" },
  { label: "Ürün Ölçü Birimleri", hint: "Adet / Kg / Litre gibi birimler (Ürünler sayfası)", kw: "ürün ölçü birim adet kg litre koli paket unit birimler", to: "/urunler" },
  { label: "Yapay Zeka (AI) Yapılandırması", hint: "AI sağlayıcı / model seçimi (Entegrasyonlar)", kw: "ai yapay zeka model gemini gpt claude asistan yapılandırma provider", to: "/entegrasyonlar" },
  { label: "Yemek Kartları", hint: "Sodexo / Multinet vb. sağlayıcılar (Entegrasyonlar)", kw: "yemek kart sodexo multinet edenred ticket setcard tokenflex meal kart", to: "/entegrasyonlar" },
  { label: "Scraper Proxy", hint: "Piyasa fiyatı toplama proxy ayarı (Fırsatlar)", kw: "proxy scraper piyasa fiyat toplama vekil sunucu ip scraping", to: "/firsatlar" },
];

// Türkiye VKN (10 hane) resmî checksum doğrulaması — competitor-level firma profili doğrulaması.
function vknValid(v) {
  if (!/^[0-9]{10}$/.test(v || "")) return false;
  const d = String(v).split("").map(Number);
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const tmp = (d[i] + (9 - i)) % 10;
    sum += tmp === 0 ? 0 : ((tmp * (2 ** (9 - i))) % 9 || 9);
  }
  return ((10 - (sum % 10)) % 10) === d[9];
}
function emailValid(v) { return !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }
function companyCompleteness(c) {
  if (!c) return 0;
  const keys = ["name", "legal_name", "tax_office", "tax_no", "phone", "email", "province", "district", "address"];
  const filled = keys.filter((k) => String(c[k] || "").trim().length > 0).length;
  return Math.round((filled / keys.length) * 100);
}

const SMS_PROVIDERS = [
  { id: "netgsm", label: "NetGSM", note: "Türkiye · Önerilen" },
  { id: "iletimerkezi", label: "İleti Merkezi", note: "Türkiye" },
  { id: "verimor", label: "Verimor", note: "Türkiye" },
  { id: "twilio", label: "Twilio", note: "Uluslararası" },
];
const SMS_FIELDS = {
  netgsm: [
    { k: "netgsm_usercode", label: "Kullanıcı Kodu / Abone No", ph: "850xxxxxxx" },
    { k: "netgsm_password", label: "API Şifresi", secret: true, setk: "netgsm_password_set" },
    { k: "netgsm_msgheader", label: "Onaylı Başlık (Gönderici)", ph: "GROOT" },
  ],
  iletimerkezi: [
    { k: "im_key", label: "API Key", ph: "key" },
    { k: "im_hash", label: "API Hash", secret: true, setk: "im_hash_set" },
    { k: "im_sender", label: "Onaylı Başlık (Gönderici)", ph: "GROOT" },
  ],
  verimor: [
    { k: "verimor_username", label: "Kullanıcı Adı", ph: "905xxxxxxxxx" },
    { k: "verimor_password", label: "API Şifresi", secret: true, setk: "verimor_password_set" },
    { k: "verimor_source", label: "Onaylı Başlık (source_addr)", ph: "GROOT" },
  ],
  twilio: [
    { k: "account_sid", label: "Account SID", ph: "ACxxxxxxxx…" },
    { k: "auth_token", label: "Auth Token", secret: true, setk: "auth_token_set" },
    { k: "from_number", label: "Gönderen Numara", ph: "+1xxxxxxxxxx" },
  ],
};
const SMS_PROVIDER_HINTS = {
  netgsm: "NetGSM panelinde bir API alt kullanıcısı açın, SMS + API yetkisi verin ve onaylı gönderici başlığını tanımlayın.",
  iletimerkezi: "İleti Merkezi panelinden API Key/Hash alın ve onaylı gönderici başlığınızı kullanın.",
  verimor: "Verimor hesabınızdan API kullanıcı adı/şifresi ve onaylı başlık (source_addr) tanımlayın.",
  twilio: "Twilio Console'dan Account SID, Auth Token ve SMS aktif bir gönderen numara alın.",
};


function Section({ children }) {
  return <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">{children}</div>;
}

export default function Settings() {
  const { can, user, refreshBranding } = useAuth();
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || null);
  const [designSub, setDesignSub] = useState("paneldesign");
  const [commsSub, setCommsSub] = useState("notifications");
  const [settingsQuery, setSettingsQuery] = useState("");
  const [settingsHi, setSettingsHi] = useState(0);
  const [devicesSub, setDevicesSub] = useState(can("dashboard.read") ? "devices" : "esl");
  const [sectorsSub, setSectorsSub] = useState("sectors");
  const [monitorSub, setMonitorSub] = useState("security");
  const [hiddenSub, setHiddenSub] = useState(new Set());
  useEffect(() => { const t = sp.get("tab"); if (t) setTab(t); }, [sp]);
  const [sms, setSms] = useState(null);
  const [notify, setNotify] = useState(null);
  const [nmatrix, setNmatrix] = useState(null);        // bildirim kırılımı matrisi
  const [roles2, setRoles2] = useState([]);
  const [secPolicy, setSecPolicy] = useState(null);
  const [company, setCompany] = useState(null);
  const [camp, setCamp] = useState({ message: "", audience: "all", test_phone: "" });
  const [counts, setCounts] = useState(null);
  const [sending, setSending] = useState(false);
  const [campResult, setCampResult] = useState(null);
  const [campHistory, setCampHistory] = useState([]);
  const [campSchedule, setCampSchedule] = useState("");
  const [credTestPhone, setCredTestPhone] = useState("");
  const [docHideBrand, setDocHideBrand] = useState(false);
  const [wkCfg, setWkCfg] = useState(null);
  const [wkBusy, setWkBusy] = useState(false);
  const [riskCfg, setRiskCfg] = useState(null);
  const [riskBusy, setRiskBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [whMode, setWhMode] = useState("both");
  const [docTheme, setDocTheme] = useState("emerald");
  const [docTemplate, setDocTemplate] = useState("klasik");
  const [docContent, setDocContent] = useState(null);
  const [docCustomPrimary, setDocCustomPrimary] = useState("");
  const [docCustomAccent, setDocCustomAccent] = useState("");
  const [loginTheme, setLoginTheme] = useState("standart");
  const [loginLayout, setLoginLayout] = useState("klasik");
  const [loginTexts, setLoginTexts] = useState(LOGIN_TEXT_DEFAULTS);
  const setTx = (k, v) => setLoginTexts((t) => ({ ...t, [k]: v }));
  const setFeat = (i, k, v) => setLoginTexts((t) => ({ ...t, features: (t.features || []).map((f, j) => j === i ? { ...f, [k]: v } : f) }));
  const setStat = (i, k, v) => setLoginTexts((t) => ({ ...t, stats: (t.stats || []).map((s, j) => j === i ? { ...s, [k]: v } : s) }));
  useEffect(() => {
    let alive = true;
    api.get("/settings/all").then((r) => {
      if (!alive) return;
      const d = r.data || {};
      if (d.hidden_modules) setHiddenSub(new Set(d.hidden_modules.hidden || []));
      if (d.branding) applyBrandingState(d.branding);
      if (d.doc_theme) { setDocTheme(d.doc_theme.theme || "emerald"); setDocTemplate(d.doc_theme.template || "klasik"); if (d.doc_theme.primary) setDocCustomPrimary(d.doc_theme.primary); if (d.doc_theme.accent) setDocCustomAccent(d.doc_theme.accent); }
      if (d.doc_templates) setDocContent(d.doc_templates);
      if (d.company_profile) setCompany(d.company_profile || {});
      if (d.notify_matrix) setNmatrix(d.notify_matrix);
      if (d.wholesale) setWhMode(d.wholesale.mode || "both");
      if (d.sms) setSms(d.sms);
      if (d.notifications) setNotify(d.notifications);
      if (d.security_policy) setSecPolicy(d.security_policy);
      if (d.roles) setRoles2(d.roles || []);
      if (d.sms_audience_counts) setCounts(d.sms_audience_counts);
      if (d.sms_campaigns) setCampHistory(d.sms_campaigns.items || []);
      if (d.weekly_summary) setWkCfg(d.weekly_summary);
    }).catch(() => {});
    api.get("/settings/daily-risk-email").then((r) => setRiskCfg(r.data)).catch(() => {});
    return () => { alive = false; };
  }, []); // eslint-disable-line
  const saveWhMode = async () => {
    setSaving(true);
    try { const { data } = await api.put("/settings/wholesale", { mode: whMode }); setWhMode(data.mode); toast.success("Toptan yetki modu kaydedildi"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const applyDocTheme = (key) => {
    setDocTheme(key);
    try {
      localStorage.setItem("groot_doc_theme", key);
      if (key !== "custom") localStorage.removeItem("groot_doc_custom");
    } catch (e) { console.debug("localStorage yazılamadı (doc-theme)", e); }
  };
  const applyDocTemplate = (key) => {
    setDocTemplate(key);
    try { localStorage.setItem("groot_doc_template", key); } catch (e) { console.debug("localStorage yazılamadı (doc-template)", e); }
  };
  const applyFontLive = (fam, sc, we) => {
    setFontFamily(fam); setFontScale(sc); setFontWeight(we);
    applyGlobalFont({ font_family: fam, font_scale: sc, font_weight: we });
  };
  const saveDocContent = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/doc-templates", docContent || {});
      setDocContent(data);
      try { localStorage.setItem("groot_doc_content", JSON.stringify(data)); } catch (e) { console.debug("localStorage yazılamadı (doc-content)", e); }
      // Önizlemede seçilen temayı da kalıcı kaydet (tek "Kaydet" hepsini uygular)
      try {
        const tbody = { theme: docTheme, template: docTemplate };
        if (docTheme === "custom") { tbody.primary = docCustomPrimary || "#064E3B"; tbody.accent = docCustomAccent || "#EA580C"; }
        await api.put("/settings/doc-theme", tbody);
        localStorage.setItem("groot_doc_theme", docTheme); localStorage.setItem("groot_doc_template", docTemplate || "klasik");
      } catch (e) { console.debug("doc-theme kaydı yerelde saklanamadı", e); }
      toast.success("Belge içeriği ve tema kaydedildi — yazdırılan belgelere yansıyacak");
      return true;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); return false; } finally { setSaving(false); }
  };
  const saveLoginTheme = async () => {
    setSaving(true);
    try {
      const clean = (v) => { const t = (v || "").trim(); return t ? t : null; };
      const body = { logo_url: brandLogo || null, brand_name: clean(brandName), primary: clean(primary), accent: clean(accent), show_on_login: showOnLogin, doc_hide_brand: docHideBrand, stamp_url: brandStamp || null, stamp_pos: brandStampPos || "center", login_theme: loginTheme || "standart", login_layout: loginLayout || "klasik", font_family: fontFamily, font_scale: fontScale, font_weight: fontWeight, login_texts: loginTexts };
      const { data } = await api.put("/settings/branding", body);
      applyBrandingState(data); await refreshBranding();
      localStorage.setItem("groot_login_theme", loginTheme || "standart");
      localStorage.setItem("groot_login_layout", loginLayout || "klasik");
      try { localStorage.setItem("groot_login_texts", JSON.stringify(loginTexts)); } catch (e) { console.warn("groot_login_texts localStorage yazılamadı:", e); }
      toast.success("Giriş ekranı tasarımı kaydedildi — giriş sayfasına yansıyacak");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const [brandLogo, setBrandLogo] = useState("");
  const [brandStamp, setBrandStamp] = useState("");
  const [brandStampPos, setBrandStampPos] = useState("center");
  const [stampBusy, setStampBusy] = useState(false);
  const saveNotify = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/notifications", { email_enabled: notify.email_enabled, sms_enabled: notify.sms_enabled });
      setNotify({ ...notify, ...data }); toast.success("Bildirim ayarları kaydedildi");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const toggleMatrix = (evKey, channel, code) => {
    setNmatrix((nm) => {
      const m = { ...(nm.matrix || {}) };
      const cur = { email: [], sms: [], ...(m[evKey] || {}) };
      const set = new Set(cur[channel] || []);
      set.has(code) ? set.delete(code) : set.add(code);
      m[evKey] = { ...cur, [channel]: [...set] };
      return { ...nm, matrix: m };
    });
  };
  const saveMatrix = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/notify-matrix", { matrix: nmatrix.matrix });
      setNmatrix({ ...nmatrix, matrix: data.matrix }); toast.success("Bildirim kırılımı kaydedildi");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const saveSecPolicy = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/security-policy", { mandatory_2fa_roles: secPolicy.mandatory_2fa_roles || [] });
      setSecPolicy(data); toast.success("Güvenlik politikası kaydedildi");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const [brandBusy, setBrandBusy] = useState(false);
  const [brandName, setBrandName] = useState("");
  const [primary, setPrimary] = useState("");
  const [accent, setAccent] = useState("");
  const [showOnLogin, setShowOnLogin] = useState(false);
  const [fontFamily, setFontFamily] = useState("sistem");
  const [fontScale, setFontScale] = useState("normal");
  const [fontWeight, setFontWeight] = useState("normal");
  const applyBrandingState = (data) => {
    setBrandLogo(data.logo_url || ""); setBrandName(data.brand_name || "");
    setPrimary(data.primary || ""); setAccent(data.accent || ""); setShowOnLogin(!!data.show_on_login); setDocHideBrand(!!data.doc_hide_brand);
    setBrandStamp(data.stamp_url || ""); setBrandStampPos(data.stamp_pos || "center");
    setLoginTheme(data.login_theme || "standart"); setLoginLayout(data.login_layout || "klasik");
    setFontFamily(data.font_family || "sistem"); setFontScale(data.font_scale || "normal"); setFontWeight(data.font_weight || "normal");
    setLoginTexts({ ...LOGIN_TEXT_DEFAULTS, ...(data.login_texts || {}) });
  };
  const saveBranding = async (overrides = {}) => {
    try {
      const clean = (v) => { const t = (v || "").trim(); return t ? t : null; };
      const body = { logo_url: brandLogo || null, brand_name: clean(brandName), primary: clean(primary), accent: clean(accent), show_on_login: showOnLogin, doc_hide_brand: docHideBrand, stamp_url: brandStamp || null, stamp_pos: brandStampPos || "center", login_theme: loginTheme || "standart", login_layout: loginLayout || "klasik", font_family: fontFamily, font_scale: fontScale, font_weight: fontWeight, login_texts: loginTexts, ...overrides };
      const { data } = await api.put("/settings/branding", body);
      applyBrandingState(data); await refreshBranding();
      toast.success("Marka ayarları kaydedildi");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const uploadBrandLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("Logo 6MB'den küçük olmalı"); e.target.value = ""; return; }
    setBrandBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await saveBranding({ logo_url: data.url });
    } catch (er) { toast.error("Logo yüklenemedi"); } finally { setBrandBusy(false); e.target.value = ""; }
  };
  const uploadBrandStamp = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 6 * 1024 * 1024) { toast.error("Görsel 6MB'den küçük olmalı"); e.target.value = ""; return; }
    setStampBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBrandStamp(data.url);
      await saveBranding({ stamp_url: data.url });
    } catch (er) { toast.error("Görsel yüklenemedi"); } finally { setStampBusy(false); e.target.value = ""; }
  };
  const sendCampaign = async (test, audienceOverride) => {
    if (!camp.message || camp.message.trim().length < 3) return toast.error("Mesaj en az 3 karakter olmalı");
    if (test && !camp.test_phone) return toast.error("Test için bir telefon numarası girin");
    const audience = audienceOverride || camp.audience;
    setSending(true); setCampResult(null);
    try {
      const body = { message: camp.message.trim(), audience, test_phone: test ? camp.test_phone : null };
      const { data } = await api.post("/sms/campaign", body);
      setCampResult(data);
      if (data.disabled) toast.warning("SMS sistemi kapalı — Ayarlar > SMS ana anahtarını açın.");
      else if (data.mocked) toast.warning(`SMS altyapısı hazır ancak sağlayıcı anahtarı yok — ${data.total} hedef, gönderim atlandı (MOCKED).`);
      else toast.success(`SMS gönderildi: ${data.sent} başarılı, ${data.skipped} atlandı (${data.total} hedef).`);
      if (!test) api.get("/sms/campaigns").then((r) => setCampHistory(r.data?.items || [])).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSending(false); }
  };

  const scheduleCampaign = async () => {
    if (!camp.message || camp.message.trim().length < 3) return toast.error("Mesaj en az 3 karakter olmalı");
    if (!campSchedule) return toast.error("Gönderim zamanı seçin");
    const iso = new Date(campSchedule).toISOString();
    setSending(true); setCampResult(null);
    try {
      const { data } = await api.post("/sms/campaign", { message: camp.message.trim(), audience: camp.audience, scheduled_at: iso });
      setCampResult(data);
      setCampSchedule("");
      toast.success("Kampanya zamanlandı — zamanı gelince otomatik gönderilecek");
      api.get("/sms/campaigns").then((r) => setCampHistory(r.data?.items || [])).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSending(false); }
  };
  const cancelCampaign = async (id) => {
    try {
      await api.delete(`/sms/campaign/${id}`);
      toast.success("Zamanlı kampanya iptal edildi");
      api.get("/sms/campaigns").then((r) => setCampHistory(r.data?.items || [])).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const sendCredTest = async () => {
    if (!credTestPhone) return toast.error("Test için telefon numarası girin");
    try {
      const { data } = await api.post("/sms/campaign", { message: "Groot ONE test SMS - kurulum dogrulama.", audience: "all", test_phone: credTestPhone });
      if (data.disabled) toast.warning("SMS sistemi kapalı — ana anahtarı açın.");
      else if (data.mocked) toast.warning("SMS sağlayıcısı anahtarı yok — test atlandı. Anahtarları kaydedip tekrar deneyin.");
      else toast.success("Test SMS gönderildi ✓ Sağlayıcı çalışıyor.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveWkCfg = async () => {
    setWkBusy(true);
    try { const { data } = await api.put("/settings/weekly-summary", wkCfg); setWkCfg(data); toast.success("Haftalık özet ayarları kaydedildi"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setWkBusy(false); }
  };
  const sendWkNow = async () => {
    setWkBusy(true);
    try { const { data } = await api.post("/reports/weekly-summary/send"); toast.success(`Özet gönderildi: ${data.sent ?? 0} alıcı`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setWkBusy(false); }
  };
  const saveRiskCfg = async () => {
    setRiskBusy(true);
    try { const { data } = await api.put("/settings/daily-risk-email", riskCfg); setRiskCfg(data); toast.success("Günlük risk e-postası ayarları kaydedildi"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setRiskBusy(false); }
  };
  const sendRiskNow = async () => {
    setRiskBusy(true);
    try { const { data } = await api.post("/reports/daily-risk/send"); toast.success(data.skipped ? `Gönderilmedi (${data.skipped})` : `Risk özeti gönderildi: ${data.sent ?? 0} alıcı`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setRiskBusy(false); }
  };

  const saveSms = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/sms", {
        enabled: sms.enabled, survey_thanks: sms.survey_thanks, survey_invite: sms.survey_invite,
        sender_name: sms.sender_name, provider: sms.provider || "netgsm",
        account_sid: sms.account_sid || "", from_number: sms.from_number || "", auth_token: sms.auth_token || "",
        netgsm_usercode: sms.netgsm_usercode || "", netgsm_msgheader: sms.netgsm_msgheader || "", netgsm_password: sms.netgsm_password || "",
        im_key: sms.im_key || "", im_sender: sms.im_sender || "", im_hash: sms.im_hash || "",
        verimor_username: sms.verimor_username || "", verimor_source: sms.verimor_source || "", verimor_password: sms.verimor_password || "",
      });
      setSms({ ...data, auth_token: "", netgsm_password: "", im_hash: "", verimor_password: "" }); toast.success("SMS ayarları kaydedildi");
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const saveCompany = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/company-profile", company || {});
      setCompany(data); setCompanyProfile(data); toast.success("Firma bilgileri kaydedildi");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const TABS = [
    ...BASE_TABS,
    ...(can("config.manage") ? [["firma", "Firma Bilgileri", Building2], ["sectors", "Sektörler & Modüller", Boxes]] : []),
    ...(can("design.manage") ? [["design", "Tasarım", Palette]] : []),
    ...(can("role.manage") ? [["integrations", "Entegrasyonlar", Plug]] : []),
    ...((can("dashboard.read") || can("esl.read")) ? [["devices", "Cihazlar & Yazıcılar", Cpu]] : []),
    ...(can("config.manage") ? [["comms", "Bildirimler", Bell]] : []),
    ...(can("user.manage") ? [["monitor", "Sistem Yönetimi", ServerCog]] : []),
    ...(user?.role === "admin" ? [["reset", "Sistem Sıfırlama", ShieldAlert]] : []),
  ];
  const visibleTABS = TABS.filter(([k]) => !hiddenSub.has(`/ayarlar#${k}`));
  useEffect(() => { if ((!tab || hiddenSub.has(`/ayarlar#${tab}`)) && visibleTABS[0]) setTab(visibleTABS[0][0]); }, [hiddenSub, tab, visibleTABS]); // eslint-disable-line
  const wide = ["integrations", "design", "comms", "monitor", "devices"].includes(tab);
  const jumpToSetting = (e) => {
    if (e.to) { window.location.assign(e.to); return; }
    setTab(e.tab);
    if (e.designSub) setDesignSub(e.designSub);
    if (e.commsSub) setCommsSub(e.commsSub);
    if (e.devicesSub) setDevicesSub(e.devicesSub);
    if (e.sectorsSub) setSectorsSub(e.sectorsSub);
    if (e.monitorSub) setMonitorSub(e.monitorSub);
    setSettingsQuery("");
    setSettingsHi(0);
  };
  const _sq = settingsQuery.trim().toLowerCase();
  const settingsMatches = _sq.length >= 1
    ? SETTINGS_INDEX.filter((e) => (e.label + " " + e.kw + " " + (e.hint || "")).toLowerCase().includes(_sq)).slice(0, 8)
    : [];

  const vknOk = vknValid(company?.tax_no || "");
  const emailOk = emailValid(company?.email || "");
  const companyPct = companyCompleteness(company);

  return (
    <div className={`mx-auto ${wide ? "max-w-[1400px]" : "max-w-3xl"} space-y-5 fade-in`} data-testid="settings-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-black tracking-tight text-slate-900">
          <Settings2 className="h-6 w-6 text-brand-green" /> Sistem Ayarları
        </h1>
        <p className="mt-1 text-sm text-slate-500">Sistem yapılandırması.</p>
      </div>

      <div className="relative max-w-md" data-testid="settings-search">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="settings-search-input" value={settingsQuery}
            onChange={(ev) => { setSettingsQuery(ev.target.value); setSettingsHi(0); }}
            onKeyDown={(ev) => {
              if (!settingsMatches.length) { if (ev.key === "Escape") setSettingsQuery(""); return; }
              if (ev.key === "ArrowDown") { ev.preventDefault(); setSettingsHi((h) => (h + 1) % settingsMatches.length); }
              else if (ev.key === "ArrowUp") { ev.preventDefault(); setSettingsHi((h) => (h - 1 + settingsMatches.length) % settingsMatches.length); }
              else if (ev.key === "Enter") { ev.preventDefault(); jumpToSetting(settingsMatches[Math.min(settingsHi, settingsMatches.length - 1)]); }
              else if (ev.key === "Escape") { ev.preventDefault(); setSettingsQuery(""); setSettingsHi(0); }
            }}
            placeholder="Ayarlarda ara: kaşe, sms, logo, fatura, kur..." className="h-11 rounded-xl pl-9" />
        </div>
        {settingsMatches.length > 0 && (
          <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg" data-testid="settings-search-results">
            {settingsMatches.map((e, i) => {
              const active = i === Math.min(settingsHi, settingsMatches.length - 1);
              return (
                <button key={i} type="button" data-testid={`settings-search-hit-${i}`} onClick={() => jumpToSetting(e)}
                  onMouseEnter={() => setSettingsHi(i)} aria-selected={active} data-active={active}
                  className={`flex w-full items-center justify-between gap-3 border-b border-slate-50 px-3 py-2.5 text-left last:border-0 ${active ? "bg-emerald-50" : "hover:bg-emerald-50/60"}`}>
                  <span className="text-sm font-semibold text-slate-800">{e.label}</span>
                  {e.hint && <span className="truncate text-[11px] text-slate-400">{e.hint}</span>}
                </button>
              );
            })}
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 text-[10px] font-medium text-slate-400" data-testid="settings-search-kbdhint">
              <kbd className="rounded border border-slate-300 bg-white px-1">↑</kbd>
              <kbd className="rounded border border-slate-300 bg-white px-1">↓</kbd> gez
              <kbd className="rounded border border-slate-300 bg-white px-1">Enter</kbd> seç
              <kbd className="rounded border border-slate-300 bg-white px-1">Esc</kbd> kapat
            </div>
          </div>
        )}
      </div>

      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-slate-400" data-testid="settings-breadcrumb">
        <span className="font-bold uppercase tracking-wide text-slate-500">Sistem Ayarları</span>
        <ChevronRight className="h-3 w-3" />
        <span className="font-bold text-brand-green">{(visibleTABS.find(([k]) => k === tab) || [0, ""])[1]}</span>
        {(tab === "design" || tab === "comms" || tab === "devices" || tab === "sectors") && (
          <><ChevronRight className="h-3 w-3" /><span className="font-semibold text-slate-600">{tab === "design" ? ({ paneldesign: "Panel Tasarımı", belge: "Belge Tasarımı", font: "Yazı Tipi", logintheme: "Giriş Ekranı" }[designSub]) : tab === "comms" ? ({ notifications: "Bildirimler", wholesale: "Toptan Yetki", sms: "SMS", weekly: "Haftalık Özet", risk: "Günlük Risk" }[commsSub]) : tab === "devices" ? ({ devices: "Cihazlar & Yazıcılar", esl: "Raf Etiketleri (ESL)" }[devicesSub]) : ({ sectors: "Sektörler", modules: "Modüller & İş Akışı" }[sectorsSub])}</span></>
        )}
      </div>
      <div className="inline-flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1" data-testid="settings-tabs">
        {visibleTABS.map(([k, l, Icon]) => (
          <button key={k} data-testid={`settings-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-bold transition-colors ${tab === k ? "bg-brand-green text-white shadow-sm" : "text-slate-500 hover:bg-white/70 hover:text-slate-700"}`}>
            <Icon className="h-4 w-4" /> {l}
          </button>
        ))}
      </div>

      {tab === "design" && can("design.manage") && (
        <div className="flex flex-wrap items-center gap-2" data-testid="design-subtabs-wrap">
          <span className="flex items-center gap-1 rounded-md bg-brand-green/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-brand-green"><CornerDownRight className="h-3 w-3" /> Tasarım</span>
          <div className="inline-flex flex-wrap rounded-lg bg-white p-1 ring-1 ring-inset ring-slate-200" data-testid="design-subtabs">
            {[["paneldesign", "Panel Tasarımı", LayoutDashboard], ["belge", "Belge Tasarımı", Receipt], ["font", "Yazı Tipi", Type], ["logintheme", "Giriş Ekranı", LogIn]].map(([k, l, Icon]) => (
              <button key={k} data-testid={`design-sub-${k}`} onClick={() => setDesignSub(k)}
                className={`flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-semibold transition-colors ${designSub === k ? "bg-brand-green/10 text-brand-green ring-1 ring-inset ring-brand-green/30" : "text-slate-500 hover:text-slate-700"}`}>
                <Icon className="h-4 w-4" /> {l}
              </button>
            ))}
          </div>
        </div>
      )}
      {tab === "comms" && (
        <div className="flex flex-wrap items-center gap-2" data-testid="comms-subtabs-wrap">
          <span className="flex items-center gap-1 rounded-md bg-brand-green/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-brand-green"><CornerDownRight className="h-3 w-3" /> Bildirimler</span>
          <div className="inline-flex flex-wrap rounded-lg bg-white p-1 ring-1 ring-inset ring-slate-200" data-testid="comms-subtabs">
            {[["notifications", "Bildirimler", Bell], ["wholesale", "Toptan Yetki", Tag], ["sms", "SMS", MessageSquare], ["weekly", "Haftalık Özet", Send], ["risk", "Günlük Risk", ShieldAlert]].map(([k, l, Icon]) => (
              <button key={k} data-testid={`comms-sub-${k}`} onClick={() => setCommsSub(k)}
                className={`flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-semibold transition-colors ${commsSub === k ? "bg-brand-green/10 text-brand-green ring-1 ring-inset ring-brand-green/30" : "text-slate-500 hover:text-slate-700"}`}>
                <Icon className="h-4 w-4" /> {l}
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === "integrations" && <div data-testid="settings-integrations-panel"><Integrations /></div>}
      {tab === "reset" && <div data-testid="settings-reset-panel" className="space-y-6"><ProductMigrationCard /><SystemReset /></div>}
      {tab === "devices" && (
        <div data-testid="settings-devices-panel">
          {(can("dashboard.read") && can("esl.read")) && (
            <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="devices-subtabs-wrap">
              <span className="flex items-center gap-1 rounded-md bg-brand-green/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-brand-green"><CornerDownRight className="h-3 w-3" /> Cihazlar &amp; Yazıcılar</span>
              <div className="inline-flex flex-wrap rounded-lg bg-white p-1 ring-1 ring-inset ring-slate-200" data-testid="devices-subtabs">
                {[["devices", "Cihazlar & Yazıcılar", Cpu], ["esl", "Raf Etiketleri (ESL)", Tag]].map(([k, l, Icon]) => (
                  <button key={k} data-testid={`devices-sub-${k}`} onClick={() => setDevicesSub(k)}
                    className={`flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-semibold transition-colors ${devicesSub === k ? "bg-brand-green/10 text-brand-green ring-1 ring-inset ring-brand-green/30" : "text-slate-500 hover:text-slate-700"}`}>
                    <Icon className="h-4 w-4" /> {l}
                  </button>
                ))}
              </div>
            </div>
          )}
          {devicesSub === "devices" && can("dashboard.read") && <div data-testid="settings-devicecfg-panel"><DeviceSettings /></div>}
          {devicesSub === "esl" && can("esl.read") && <div data-testid="settings-esl-panel"><Esl /></div>}
        </div>
      )}
      {tab === "monitor" && <div data-testid="settings-monitor-panel"><SystemMonitor initialSub={monitorSub} /></div>}
      {tab === "sectors" && (
        <div data-testid="settings-sectors-panel" className="space-y-4">
          <div className="flex flex-wrap items-center gap-2" data-testid="sectors-subtabs-wrap">
            <span className="flex items-center gap-1 rounded-md bg-brand-green/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-brand-green"><CornerDownRight className="h-3 w-3" /> Sektörler &amp; Modüller</span>
            <div className="inline-flex flex-wrap rounded-lg bg-white p-1 ring-1 ring-inset ring-slate-200" data-testid="sectors-subtabs">
              {[["sectors", "Sektörler", Layers], ["modules", "Modüller & İş Akışı", SlidersHorizontal]].map(([k, l, Icon]) => (
                <button key={k} data-testid={`sectors-sub-${k}`} onClick={() => setSectorsSub(k)}
                  className={`flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-semibold transition-colors ${sectorsSub === k ? "bg-brand-green/10 text-brand-green ring-1 ring-inset ring-brand-green/30" : "text-slate-500 hover:text-slate-700"}`}>
                  <Icon className="h-4 w-4" /> {l}
                </button>
              ))}
            </div>
          </div>
          {sectorsSub === "sectors" && <SectorsSettings />}
          {sectorsSub === "modules" && <BusinessFeaturesSettings />}
        </div>
      )}
      {/* not: kullanılmayan 'urun' sekmesi kaldırıldı */}

      {tab === "firma" && (
        <Section>
          <p className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><Building2 className="h-5 w-5 text-brand-green" /> Firma Bilgileri (Tek Kaynak)</p>
          <p className="mt-1 text-sm text-slate-500">Firma bilgilerini burada bir kez tanımlayın. Lokasyon vb. formlarda <b>"Firma Getir"</b> ile bu bilgiler otomatik doldurulur — her yere elle yazmanıza gerek kalmaz.</p>
          {!company ? <p className="mt-4 text-sm text-slate-400">Yükleniyor…</p> : (
            <>
              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/70 p-3" data-testid="company-completeness">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-slate-600">Profil tamamlanma</span>
                  <span data-testid="company-completeness-pct" className={`font-bold ${companyPct === 100 ? "text-emerald-600" : companyPct >= 60 ? "text-amber-600" : "text-rose-600"}`}>%{companyPct}</span>
                </div>
                <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                  <div className={`h-full transition-all ${companyPct === 100 ? "bg-emerald-500" : companyPct >= 60 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${companyPct}%` }} />
                </div>
                {companyPct < 100 && <p className="mt-1.5 text-[11px] text-slate-400">Eksiksiz firma profili; belge/fatura başlıklarının ve e-Belge alıcı bilgilerinin doğru dolmasını sağlar.</p>}
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="company-profile-form">
                <div><Label>Firma Adı</Label><Input data-testid="company-name" value={company.name || ""} onChange={(e) => setCompany({ ...company, name: e.target.value })} className="mt-1" placeholder="Örn: Groot Market" /></div>
                <div><Label>Ünvan (Yasal Ad)</Label><Input data-testid="company-legal" value={company.legal_name || ""} onChange={(e) => setCompany({ ...company, legal_name: e.target.value })} className="mt-1" placeholder="Örn: Groot Games Teknoloji A.Ş." /></div>
                <div><Label>Vergi Dairesi</Label><TaxOfficePicker testid="company-taxoffice" value={company.tax_office || ""} onChange={(v) => setCompany({ ...company, tax_office: v })} /></div>
                <div>
                  <Label>Vergi No (VKN)</Label>
                  <Input data-testid="company-taxno" value={company.tax_no || ""} onChange={(e) => setCompany({ ...company, tax_no: e.target.value.replace(/\D/g, "") })} className="mt-1 tabular" maxLength={10} placeholder="10 hane" />
                  {(company.tax_no || "").length > 0 && (
                    <p data-testid="company-taxno-hint" className={`mt-1 flex items-center gap-1 text-[11px] ${vknOk ? "text-emerald-600" : "text-rose-500"}`}>
                      {vknOk ? <><CheckCircle2 className="h-3 w-3" /> Geçerli VKN</> : <><AlertTriangle className="h-3 w-3" /> {(company.tax_no || "").length !== 10 ? "10 hane olmalı" : "VKN doğrulaması tutmuyor"}</>}
                    </p>
                  )}
                </div>
                <div><Label>Telefon</Label><Input data-testid="company-phone" value={company.phone || ""} onChange={(e) => setCompany({ ...company, phone: e.target.value })} className="mt-1" placeholder="0216 000 00 00" /></div>
                <div>
                  <Label>E-posta</Label>
                  <Input data-testid="company-email" value={company.email || ""} onChange={(e) => setCompany({ ...company, email: e.target.value })} className="mt-1" placeholder="info@firma.com" />
                  {(company.email || "").length > 0 && !emailOk && <p data-testid="company-email-hint" className="mt-1 flex items-center gap-1 text-[11px] text-rose-500"><AlertTriangle className="h-3 w-3" /> Geçerli e-posta girin</p>}
                </div>
                <div><Label>İl</Label><Input data-testid="company-province" value={company.province || ""} onChange={(e) => setCompany({ ...company, province: e.target.value })} className="mt-1" /></div>
                <div><Label>İlçe</Label><Input data-testid="company-district" value={company.district || ""} onChange={(e) => setCompany({ ...company, district: e.target.value })} className="mt-1" /></div>
                <div className="sm:col-span-2"><Label>Açık Adres</Label><Input data-testid="company-address" value={company.address || ""} onChange={(e) => setCompany({ ...company, address: e.target.value })} className="mt-1" /></div>
                <div className="sm:col-span-2"><Label>Yetkili Kişi</Label><Input data-testid="company-authorized" value={company.authorized_person || ""} onChange={(e) => setCompany({ ...company, authorized_person: e.target.value })} className="mt-1" placeholder="Sorumlu ad soyad" /></div>
                <div><Label>Web Sitesi</Label><Input data-testid="company-web" value={company.web || ""} onChange={(e) => setCompany({ ...company, web: e.target.value })} className="mt-1" placeholder="Örn: www.firmaniz.com" /></div>
                <div><Label>Slogan / Motto</Label><Input data-testid="company-slogan" value={company.slogan || ""} onChange={(e) => setCompany({ ...company, slogan: e.target.value })} className="mt-1" placeholder="Örn: Taze · Uygun · Güvenilir" /></div>
                <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="company-bank-section">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-sm font-semibold text-slate-700">Banka Bilgileri (Fatura altında gösterilir)</Label>
                    <Button type="button" size="sm" variant="outline" data-testid="company-bank-add" onClick={() => setCompany({ ...company, bank_accounts: [...(company.bank_accounts || []), { bank_name: "", iban: "" }] })} className="h-8 border-brand-green text-brand-green hover:bg-brand-green/10">+ Banka Ekle</Button>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-400">Banka adı ve IBAN girin; birden fazla hesap ekleyebilirsiniz. Girdikleriniz Satış Faturası'nın en altında otomatik görünür.</p>
                  {(company.bank_accounts || []).length === 0 && <p className="mt-2 text-xs text-slate-400" data-testid="company-bank-empty">Henüz banka eklenmedi — varsayılan banka bilgisi kullanılır.</p>}
                  <div className="mt-2 space-y-2">
                    {(company.bank_accounts || []).map((b, i) => (
                      <div key={i} data-testid={`company-bank-row-${i}`} className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 bg-white p-2 sm:grid-cols-[1fr_1.2fr_1.5fr_auto]">
                        <Input data-testid={`company-bank-name-${i}`} value={b.bank_name || ""} placeholder="Banka Adı (Örn: Ziraat Bankası)" onChange={(e) => { const arr = [...(company.bank_accounts || [])]; arr[i] = { ...arr[i], bank_name: e.target.value }; setCompany({ ...company, bank_accounts: arr }); }} />
                        <Input data-testid={`company-bank-account-${i}`} value={b.account_name || ""} placeholder="Hesap/Ünvan (opsiyonel)" onChange={(e) => { const arr = [...(company.bank_accounts || [])]; arr[i] = { ...arr[i], account_name: e.target.value }; setCompany({ ...company, bank_accounts: arr }); }} />
                        <Input data-testid={`company-bank-iban-${i}`} value={b.iban || ""} placeholder="IBAN (TR..)" onChange={(e) => { const arr = [...(company.bank_accounts || [])]; arr[i] = { ...arr[i], iban: e.target.value.toUpperCase() }; setCompany({ ...company, bank_accounts: arr }); }} />
                        <Button type="button" size="sm" variant="ghost" data-testid={`company-bank-del-${i}`} onClick={() => { const arr = [...(company.bank_accounts || [])]; arr.splice(i, 1); setCompany({ ...company, bank_accounts: arr }); }} className="text-rose-500 hover:bg-rose-50 hover:text-rose-600">Sil</Button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
                  <Button data-testid="company-save" onClick={saveCompany} disabled={saving} className="bg-brand-green text-white hover:bg-brand-green-600">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Kaydet
                  </Button>
                  {(company.tax_no || "").length > 0 && !vknOk && <span className="text-[11px] text-amber-600">VKN geçersiz görünüyor — yine de kaydedebilirsiniz.</span>}
                </div>
              </div>
            </>
          )}
        </Section>
      )}

      {tab === "firma" && can("design.manage") && (
        <Section>
          <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-bold text-slate-800"><ImageIcon className="h-5 w-5 text-brand-green" /> Firma Logosu &amp; Marka Renkleri</h2>
          <p className="mb-4 text-sm text-slate-500">Kendi logonuzu yükleyin ve marka renklerinizi seçin — kenar çubuğu, başlık ve menüye canlı yansır. Boş bırakırsanız resmi Groot görünümü kullanılır. <b>Yalnız siz değiştirirseniz değişir.</b> (Kaşe &amp; İmza ve belge marka adı <b>Belge Tasarımı</b> sekmesindedir.)</p>
          <div className="flex flex-wrap items-center gap-5">
            <div className="flex h-20 min-w-[190px] items-center justify-center rounded-xl bg-sidebar-deep px-4" data-testid="firma-logo-preview">
              {brandLogo
                ? <img src={fileUrl(brandLogo)} alt="Logo" className="max-h-14 w-auto max-w-[200px] object-contain" data-testid="firma-logo-preview-img" />
                : <FirmLogo size={30} />}
            </div>
            <div className="flex flex-col gap-2">
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                <ImageIcon className="h-4 w-4" /> {brandBusy ? "Yükleniyor..." : "Firma Logosu Yükle"}
                <input data-testid="firma-logo-upload" type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={uploadBrandLogo} />
              </label>
              {brandLogo && <button data-testid="firma-logo-remove" onClick={() => saveBranding({ logo_url: null })} className="text-xs font-medium text-red-500 hover:underline">Logoyu kaldır (varsayılana dön)</button>}
            </div>
            <label className="flex cursor-pointer items-center gap-2 self-start rounded-lg bg-slate-50 px-3 py-2.5 ring-1 ring-inset ring-slate-200">
              <Switch data-testid="firma-show-login" checked={showOnLogin} onCheckedChange={(v) => { setShowOnLogin(v); saveBranding({ show_on_login: v }); }} />
              <span className="text-sm font-medium text-slate-700">Logoyu giriş ekranında göster</span>
            </label>
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Ana Renk (primary)</Label>
              <div className="mt-1 flex items-center gap-2">
                <input type="color" data-testid="firma-primary" value={primary || "#064E3B"} onChange={(e) => setPrimary(e.target.value)} className="h-9 w-12 shrink-0 cursor-pointer rounded border border-slate-300 bg-white p-0.5" />
                <Input value={primary} onChange={(e) => setPrimary(e.target.value)} placeholder="#064E3B (boş = varsayılan)" className="font-mono" />
              </div>
            </div>
            <div>
              <Label>Vurgu Rengi (accent)</Label>
              <div className="mt-1 flex items-center gap-2">
                <input type="color" data-testid="firma-accent" value={accent || "#EA580C"} onChange={(e) => setAccent(e.target.value)} className="h-9 w-12 shrink-0 cursor-pointer rounded border border-slate-300 bg-white p-0.5" />
                <Input value={accent} onChange={(e) => setAccent(e.target.value)} placeholder="#EA580C (boş = varsayılan)" className="font-mono" />
              </div>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Button data-testid="firma-branding-save" onClick={() => saveBranding()} className="bg-brand-green text-white hover:bg-brand-green-600"><Save className="mr-1.5 h-4 w-4" /> Logo &amp; Renkleri Kaydet</Button>
            {(primary || accent) && (
              <button data-testid="firma-reset-colors" onClick={() => { setPrimary(""); setAccent(""); saveBranding({ primary: null, accent: null }); }} className="text-xs font-medium text-red-500 hover:underline">Renkleri sıfırla (varsayılana dön)</button>
            )}
          </div>
        </Section>
      )}

      {tab === "comms" && commsSub === "notifications" && (
        <Section>
          <p className="font-heading text-lg font-bold text-slate-900">Bildirim Ayarları</p>
          <p className="text-sm text-slate-500">E-posta ve SMS gönderiminin ana anahtarları. Kapalıyken sistem o kanaldan otomatik bildirim/kampanya göndermez. Güvenlik giriş kodları (iki adımlı doğrulama) bu ayarlardan bağımsız her zaman çalışır.</p>
          {!notify ? <p className="mt-4 text-sm text-slate-400">Yükleniyor…</p> : (
            <div className="mt-4 space-y-3" data-testid="notify-settings">
              <div className={`flex items-center justify-between rounded-lg px-4 py-3 ring-1 ring-inset ${notify.email_enabled ? "bg-emerald-50 ring-emerald-200" : "bg-red-50 ring-red-200"}`}>
                <div className="min-w-0 pr-3">
                  <Label className="mb-0 flex items-center gap-1.5 font-bold"><Mail className="h-4 w-4 text-slate-400" /> E-posta bildirimleri</Label>
                  <p className="text-[11px] text-slate-500">{notify.email_available ? "Yönetim bildirimleri, onay talepleri ve özet e-postaları." : "E-posta altyapısı yapılandırılmamış."}</p>
                </div>
                <Switch data-testid="notify-email-switch" checked={!!notify.email_enabled} onCheckedChange={(v) => setNotify({ ...notify, email_enabled: v })} />
              </div>
              <div className={`flex items-center justify-between rounded-lg px-4 py-3 ring-1 ring-inset ${notify.sms_enabled ? "bg-emerald-50 ring-emerald-200" : "bg-red-50 ring-red-200"}`}>
                <div className="min-w-0 pr-3">
                  <Label className="mb-0 flex items-center gap-1.5 font-bold"><MessageSquare className="h-4 w-4 text-slate-400" /> SMS bildirimleri</Label>
                  <p className="text-[11px] text-slate-500">{notify.sms_configured ? "SMS sağlayıcısı yapılandırıldı — canlı SMS gönderilir." : "SMS sağlayıcısı anahtarı yok — gönderimler atlanır (SMS Sağlayıcısı sekmesinden girin)."}</p>
                </div>
                <Switch data-testid="notify-sms-switch" checked={!!notify.sms_enabled} onCheckedChange={(v) => setNotify({ ...notify, sms_enabled: v })} />
              </div>
              <Button data-testid="notify-save" onClick={saveNotify} disabled={saving} className="bg-brand-green text-white hover:bg-brand-green-600"><Save className="mr-1.5 h-4 w-4" /> Kaydet</Button>
            </div>
          )}

          <div className="mt-6 grid items-start gap-5 border-t border-slate-100 pt-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 p-4" data-testid="mandatory-2fa">
            <Label className="mb-0 flex items-center gap-1.5 font-bold"><ShieldAlert className="h-4 w-4 text-slate-400" /> Zorunlu İki Adımlı Doğrulama</Label>
            <p className="text-[11px] text-slate-500">Seçili roldeki kullanıcılar girişte iki adımlı doğrulamayı tamamlamak zorundadır. Kayıtlı değilse ilk girişte otomatik olarak e-posta 2FA ile zorlanır.</p>
            {!secPolicy ? <p className="mt-3 text-sm text-slate-400">Yükleniyor…</p> : (
              <>
                <div className="mt-3 flex flex-wrap gap-2">
                  {roles2.map((r) => {
                    const code = r.code || r.id || r.role || r;
                    const label = r.label || r.name || code;
                    const on = (secPolicy.mandatory_2fa_roles || []).includes(code);
                    return (
                      <button key={code} type="button" data-testid={`m2fa-${code}`}
                        onClick={() => setSecPolicy((sp) => ({ ...sp, mandatory_2fa_roles: on ? (sp.mandatory_2fa_roles || []).filter((x) => x !== code) : [...(sp.mandatory_2fa_roles || []), code] }))}
                        className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset transition-colors ${on ? "bg-brand-green text-white ring-brand-green" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <Button data-testid="m2fa-save" onClick={saveSecPolicy} disabled={saving} variant="outline" className="mt-3 border-brand-green text-brand-green hover:bg-brand-green/5"><Save className="mr-1.5 h-4 w-4" /> Politikayı Kaydet</Button>
              </>
            )}
          </div>

            <div className="rounded-xl border border-slate-200 p-4" data-testid="notify-matrix">
            <Label className="mb-0 flex items-center gap-1.5 font-bold"><Bell className="h-4 w-4 text-slate-400" /> Bildirim Kırılımı (Olay → Rol → Kanal)</Label>
            <p className="text-[11px] text-slate-500">Hangi olay gerçekleştiğinde hangi role hangi kanaldan (E-posta / SMS) bildirim gitsin? Örn: "Onay Talebi" → Yönetici e-posta + Mağaza Müdürü SMS. Seçili değilse yalnız varsayılan alıcılar bilgilendirilir.</p>
            {!nmatrix ? <p className="mt-3 text-sm text-slate-400">Yükleniyor…</p> : (
              <>
                <div className="mt-3 space-y-3">
                  {(nmatrix.events || []).map((ev) => {
                    const row = nmatrix.matrix?.[ev.key] || {};
                    return (
                      <div key={ev.key} data-testid={`nmatrix-event-${ev.key}`} className="rounded-lg border border-slate-200 p-3">
                        <p className="mb-2 text-sm font-bold text-slate-800">{ev.label}</p>
                        {[["email", "E-posta", Mail], ["sms", "SMS", MessageSquare]].map(([ch, chLabel, ChIcon]) => (
                          <div key={ch} className="mb-2 last:mb-0">
                            <p className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400"><ChIcon className="h-3 w-3" /> {chLabel}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {roles2.map((r) => {
                                const code = r.code || r.id || r.role || r;
                                const label = r.label || r.name || code;
                                const on = (row[ch] || []).includes(code);
                                return (
                                  <button key={code} type="button" data-testid={`nmatrix-${ev.key}-${ch}-${code}`}
                                    onClick={() => toggleMatrix(ev.key, ch, code)}
                                    className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset transition-colors ${on ? (ch === "email" ? "bg-brand-green text-white ring-brand-green" : "bg-brand-orange text-white ring-brand-orange") : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50"}`}>
                                    {label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
                <Button data-testid="nmatrix-save" onClick={saveMatrix} disabled={saving} variant="outline" className="mt-3 border-brand-green text-brand-green hover:bg-brand-green/5"><Save className="mr-1.5 h-4 w-4" /> Kırılımı Kaydet</Button>
              </>
            )}
            </div>
          </div>
        </Section>
      )}

      {tab === "comms" && commsSub === "wholesale" && (
        <Section>
          <h2 className="mb-1 font-heading text-lg font-bold text-slate-800">Toptan (Fırsatlı) Satış Yetkilendirme</h2>
          <p className="mb-4 text-sm text-slate-500">Kasada toptan fiyata geçiş için hangi doğrulama yöntemi kullanılsın? Tek yöntem seçerek akışı sadeleştirin.</p>
          <div className="space-y-2" data-testid="wholesale-mode-group">
            {[["both", "İkisi de — SMS kodu veya amir onayı"], ["sms", "Sadece müşteri SMS kodu"], ["supervisor", "Sadece amir onayı"]].map(([k, l]) => (
              <label key={k} className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${whMode === k ? "border-brand-green bg-brand-green/5" : "border-slate-200 hover:border-slate-300"}`}>
                <input data-testid={`wholesale-mode-${k}`} type="radio" name="whmode" checked={whMode === k} onChange={() => setWhMode(k)} className="h-4 w-4 text-brand-green" />
                <span className="text-sm font-medium text-slate-700">{l}</span>
              </label>
            ))}
          </div>
          <Button data-testid="wholesale-mode-save" onClick={saveWhMode} disabled={saving} className="mt-4 bg-brand-green text-white hover:bg-brand-green-600"><Save className="mr-1.5 h-4 w-4" /> Kaydet</Button>
        </Section>
      )}

      {tab === "design" && can("design.manage") && designSub === "font" && (
        <Section>
          <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-bold text-slate-800"><Type className="h-5 w-5 text-brand-green" /> Yazı Tipi (Global)</h2>
          <p className="mb-4 text-sm text-slate-500">Uygulama arayüzünde ve belge önizlemelerinde kullanılacak <b>yazı tipi</b>, <b>boyut</b> ve <b>kalınlık</b>. Seçim anında uygulanır; "Kaydet" ile kalıcı olur. "Varsayılan" her zaman güvenlidir.</p>
          <div className="grid gap-6 lg:grid-cols-3">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Yazı Tipi</p>
              <div className="grid gap-2" data-testid="font-family-group">
                {Object.entries(UI_FONTS).map(([k, f]) => (
                  <button key={k} type="button" data-testid={`font-family-${k}`} onClick={() => applyFontLive(k, fontScale, fontWeight)}
                    style={{ fontFamily: f.stack || undefined }}
                    className={`flex items-center justify-between rounded-xl border p-3 text-left transition-colors ${fontFamily === k ? "border-brand-green ring-2 ring-brand-green/30 bg-brand-green/5" : "border-slate-200 hover:border-slate-300"}`}>
                    <span className="text-sm font-bold text-slate-800">{f.name}</span>
                    {fontFamily === k && <CheckCircle2 className="h-5 w-5 shrink-0 text-brand-green" />}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Boyut</p>
              <div className="grid gap-2" data-testid="font-scale-group">
                {[["kucuk", "Küçük"], ["normal", "Normal"], ["buyuk", "Büyük"], ["cokbuyuk", "Çok Büyük"]].map(([k, l]) => (
                  <button key={k} type="button" data-testid={`font-scale-${k}`} onClick={() => applyFontLive(fontFamily, k, fontWeight)}
                    className={`flex items-center justify-between rounded-xl border p-3 text-left transition-colors ${fontScale === k ? "border-brand-green ring-2 ring-brand-green/30 bg-brand-green/5" : "border-slate-200 hover:border-slate-300"}`}>
                    <span className="text-sm font-bold text-slate-800">{l}</span>
                    {fontScale === k && <CheckCircle2 className="h-5 w-5 shrink-0 text-brand-green" />}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Kalınlık</p>
              <div className="grid gap-2" data-testid="font-weight-group">
                {[["normal", "Normal"], ["orta", "Orta"], ["kalin", "Kalın"]].map(([k, l]) => (
                  <button key={k} type="button" data-testid={`font-weight-${k}`} onClick={() => applyFontLive(fontFamily, fontScale, k)}
                    className={`flex items-center justify-between rounded-xl border p-3 text-left transition-colors ${fontWeight === k ? "border-brand-green ring-2 ring-brand-green/30 bg-brand-green/5" : "border-slate-200 hover:border-slate-300"}`}>
                    <span className="text-sm text-slate-800" style={{ fontWeight: UI_FONT_WEIGHTS[k] }}>{l}</span>
                    {fontWeight === k && <CheckCircle2 className="h-5 w-5 shrink-0 text-brand-green" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="font-preview">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Canlı Önizleme</p>
            <p className="text-slate-800">Groot ONE — Bulut ERP · Hızlı kasa, stok ve raporlama tek çekirdekte. Toplam: 1.234,56 ₺ · 0123456789</p>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button data-testid="font-save" onClick={() => saveBranding()} disabled={saving} className="bg-brand-green text-white hover:bg-brand-green-600">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Kaydet
            </Button>
            <button type="button" data-testid="font-reset" onClick={() => applyFontLive("sistem", "normal", "normal")} className="text-xs font-medium text-red-500 hover:underline">Varsayılana döndür</button>
          </div>
        </Section>
      )}

      {tab === "design" && can("design.manage") && designSub === "belge" && (
        <div className="space-y-6">
          <Section>
            <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-bold text-slate-800"><Receipt className="h-5 w-5 text-brand-green" /> Belge Markası — Kaşe, İmza & Marka Adı</h2>
            <p className="mb-4 text-sm text-slate-500">Belgelerde (fiş / fatura / irsaliye / makbuz) görünecek <b>kaşe &amp; imza görselini</b>, <b>marka adını</b> ve görünüm tercihlerini buradan yönetin. Firma logosu &amp; renkleri <b>Firma Bilgileri</b> sekmesindedir.</p>

            <div className="rounded-xl border border-slate-200 p-4" data-testid="branding-stamp-card">
              <h3 className="flex items-center gap-2 text-sm font-bold text-slate-800"><Receipt className="h-4 w-4 text-brand-green" /> Kaşe &amp; İmza (belgelerde)</h3>
              <p className="mt-1 text-[11px] text-slate-400">Tek görsel — tüm belgeler (fiş/fatura/irsaliye/makbuz) bunu paylaşır. Hangi belgede görüneceğini aşağıdaki <b>Belge İçeriği</b> bölümünden her belge türü için aç/kapatırsınız. Standart ebatta basılır; büyük görsel otomatik sığdırılır. Şeffaf zeminli PNG önerilir.</p>
              <div className="mt-3 flex flex-wrap items-center gap-4">
                {brandStamp
                  ? <img src={fileUrl(brandStamp)} alt="Kaşe & İmza" data-testid="branding-stamp-preview" className="h-16 w-auto max-w-[130px] rounded border border-slate-200 bg-white object-contain p-1" />
                  : <div className="flex h-16 w-16 items-center justify-center rounded border border-dashed border-slate-300 text-center text-[10px] leading-tight text-slate-400">Görsel<br />yok</div>}
                <div className="flex flex-col items-start gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                    <Upload className="h-4 w-4" /> {stampBusy ? "Yükleniyor..." : "Kaşe & İmza Yükle"}
                    <input data-testid="branding-stamp-upload" type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={uploadBrandStamp} />
                  </label>
                  {brandStamp && <button data-testid="branding-stamp-remove" onClick={() => { setBrandStamp(""); saveBranding({ stamp_url: null }); }} className="text-xs font-medium text-red-500 hover:underline">Görseli kaldır</button>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-500">Konum:</span>
                  {[["top", "Üst Orta"], ["left", "Sol"], ["center", "Orta"], ["right", "Sağ"]].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`branding-stamp-pos-${v}`} onClick={() => { setBrandStampPos(v); saveBranding({ stamp_pos: v }); }}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${brandStampPos === v ? "border-brand-green bg-brand-green text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{l}</button>
                  ))}
                </div>
                {brandStamp && (
                  <div className="w-full" data-testid="branding-stamp-mini-preview">
                    <p className="mb-1 text-[11px] font-medium text-slate-400">Belgede konum önizlemesi</p>
                    <div className="rounded-md border border-slate-200 bg-white p-3" style={{ maxWidth: "260px" }}>
                      {brandStampPos === "top" && (
                        <div style={{ textAlign: "center" }} className="mb-2">
                          <img src={fileUrl(brandStamp)} alt="Kaşe" className="inline-block" style={{ maxHeight: "44px", maxWidth: "90px" }} />
                        </div>
                      )}
                      <div className="mb-2 h-1.5 w-2/3 rounded bg-slate-100" />
                      <div className="mb-1 h-1 w-full rounded bg-slate-100" />
                      <div className="mb-1 h-1 w-5/6 rounded bg-slate-100" />
                      <div className="mb-3 h-1 w-4/6 rounded bg-slate-100" />
                      {brandStampPos !== "top" && (
                        <div style={{ textAlign: brandStampPos === "left" ? "left" : brandStampPos === "right" ? "right" : "center" }}>
                          <img src={fileUrl(brandStamp)} alt="Kaşe" className="inline-block" style={{ maxHeight: "44px", maxWidth: "90px" }} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Belge / Fiş Marka Adı (opsiyonel)</Label>
                <Input data-testid="branding-name" value={brandName} maxLength={40} onChange={(e) => setBrandName(e.target.value)} placeholder="Örn: Groot Market (boş = firma adı)" className="mt-1" />
                <p className="mt-1 text-[11px] text-slate-400">Fiş, fatura ve dekontlarda logonun altında görünür. Boş bırakırsanız firma adı kullanılır; kısaltma da yazabilirsiniz.</p>
              </div>
              <label className="flex cursor-pointer items-center justify-between self-start rounded-lg bg-slate-50 px-3 py-2.5 ring-1 ring-inset ring-slate-200">
                <span className="text-sm font-medium text-slate-700">Belgelerde sadece logo göster (marka yazısını gizle)</span>
                <Switch data-testid="branding-doc-hide" checked={docHideBrand} onCheckedChange={setDocHideBrand} />
              </label>
            </div>
            <div className="mt-4">
              <Button data-testid="branding-save" onClick={() => saveBranding()} className="bg-brand-green text-white hover:bg-brand-green-600"><Save className="mr-1.5 h-4 w-4" /> Belge Markasını Kaydet</Button>
            </div>
          </Section>

          <DocContentEditor docContent={docContent} setDocContent={setDocContent}
            docTheme={docTheme} onThemeChange={applyDocTheme}
            docTemplate={docTemplate} onTemplateChange={applyDocTemplate}
            docCustomPrimary={docCustomPrimary} setDocCustomPrimary={setDocCustomPrimary}
            docCustomAccent={docCustomAccent} setDocCustomAccent={setDocCustomAccent}
            saving={saving} onSave={saveDocContent}
            brandStamp={brandStamp} brandStampPos={brandStampPos} />
        </div>
      )}

      {tab === "design" && can("design.manage") && designSub === "logintheme" && (
        <Section>
          <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-bold text-slate-800"><LogIn className="h-5 w-5 text-brand-green" /> Giriş Ekranı Tasarımı</h2>
          <p className="mb-4 text-sm text-slate-500">Giriş sayfasının <b>renk teması</b> ve <b>yerleşim şablonu</b>. "Standart" ve "Klasik" varsayılandır; diğerleri yalnızca görünüm varyantıdır (form ve marka bozulmaz). Firma logosunu <b>Firma Bilgileri</b> sekmesinden yükleyip "giriş ekranında göster" seçeneğini oradan açabilirsiniz.</p>
          <div className="mb-4 rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500 ring-1 ring-inset ring-slate-200">Program Logosu (tüm müşterilerde görünen varsayılan logo) artık yalnızca <b>Sistem İzleme → Platform Yönetimi</b> altında, Big BOSS tarafından yönetilir.</div>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Renk Teması</p>
                <div className="grid grid-cols-2 gap-2" data-testid="logintheme-group">
                  {Object.entries(LOGIN_THEMES).map(([key, t]) => (
                    <button key={key} type="button" data-testid={`logintheme-${key}`} onClick={() => setLoginTheme(key)}
                      className={`flex w-full items-center gap-2 rounded-lg border p-2 text-left transition-colors ${loginTheme === key ? "border-brand-green ring-2 ring-brand-green/30 bg-brand-green/5" : "border-slate-200 hover:border-slate-300"}`}>
                      <span className="flex gap-0.5">
                        {["--lb1", "--lb2", "--lb3"].map((v) => <span key={v} className="h-5 w-5 rounded ring-1 ring-inset ring-black/5" style={{ background: t.vars[v] }} />)}
                      </span>
                      <p className="min-w-0 flex-1 truncate text-xs font-bold text-slate-800">{t.name}</p>
                      {loginTheme === key && <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-green" />}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Yerleşim Şablonu</p>
                <div className="grid grid-cols-2 gap-2" data-testid="loginlayout-group">
                  {Object.entries(LOGIN_LAYOUTS).map(([key, t]) => (
                    <button key={key} type="button" data-testid={`loginlayout-${key}`} onClick={() => setLoginLayout(key)}
                      className={`flex w-full items-center gap-2 rounded-lg border p-2 text-left transition-colors ${loginLayout === key ? "border-brand-green ring-2 ring-brand-green/30 bg-brand-green/5" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-bold text-slate-800">{t.name}</p>
                        <p className="truncate text-[10px] text-slate-400">{t.desc}</p>
                      </div>
                      {loginLayout === key && <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-green" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div data-testid="logintheme-preview-wrap">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Canlı Önizleme</p>
              <div className="relative h-56 overflow-hidden rounded-2xl bg-[#04241C]" data-testid="logintheme-preview" data-theme={loginTheme} data-layout={loginLayout}>
                <div className="pointer-events-none absolute -left-10 -top-8 h-40 w-40 rounded-full opacity-60 blur-2xl" style={{ background: LOGIN_THEMES[loginTheme].vars["--lb1"] }} />
                <div className="pointer-events-none absolute -right-8 top-1/3 h-32 w-32 rounded-full opacity-40 blur-2xl" style={{ background: LOGIN_THEMES[loginTheme].vars["--lb2"] }} />
                <div className="pointer-events-none absolute bottom-0 left-1/4 h-28 w-28 rounded-full opacity-30 blur-2xl" style={{ background: LOGIN_THEMES[loginTheme].vars["--lb3"] }} />
                <div className={`relative z-10 flex h-full flex-col justify-between p-4 ${loginLayout === "merkez" ? "items-center text-center" : ""}`}>
                  <span className="inline-block w-max rounded-md bg-white/90 px-2 py-1 text-[10px] font-bold text-slate-800">LOGO</span>
                  <div className="w-full">
                    <p className="font-heading text-xl font-black leading-tight text-white">{loginTexts.title}<br />{loginTexts.title_accent}</p>
                    {loginLayout === "klasik" && (
                      <div className="mt-3 grid grid-cols-2 gap-1.5">
                        {["POS", "Depo", "E-Tic.", "Lojistik"].map((x) => <div key={x} className="rounded-lg border border-white/15 bg-white/[0.07] px-2 py-1.5 text-[10px] font-bold text-white">{x}</div>)}
                      </div>
                    )}
                    {loginLayout === "liste" && (
                      <div className="mt-3 space-y-1">
                        {["Perakende POS", "Depo & Stok", "E-Ticaret"].map((x) => <div key={x} className="rounded-md border border-white/10 bg-white/[0.05] px-2 py-1 text-[10px] font-semibold text-white">{x}</div>)}
                      </div>
                    )}
                    {loginLayout === "istatistik" && (
                      <div className="mt-3 grid grid-cols-3 gap-1.5">
                        {[["1.600+", "Ürün"], ["7/24", "Bulut"], ["Çoklu", "Lokasyon"]].map(([b, s]) => <div key={s} className="rounded-lg border border-white/15 bg-white/[0.07] px-1.5 py-1.5"><p className="text-[13px] font-black text-brand-orange">{b}</p><p className="text-[8px] text-white/70">{s}</p></div>)}
                      </div>
                    )}
                    {loginLayout === "kartust" && (
                      <div className="mt-3 flex flex-wrap gap-1">{["POS", "Depo", "E-Tic.", "Loj."].map((x) => <span key={x} className="rounded-full border border-white/15 bg-white/[0.07] px-2 py-0.5 text-[9px] font-bold text-white">{x}</span>)}</div>
                    )}
                    {loginLayout === "bolme" && (
                      <div className="mt-3 grid grid-cols-2 gap-1">{["Perakende", "Depo", "E-Ticaret", "Lojistik"].map((x) => <div key={x} className="rounded-md border border-white/10 bg-white/[0.05] px-2 py-1 text-[9px] font-semibold text-white">{x}</div>)}</div>
                    )}
                    {loginLayout === "vitrin" && (
                      <div className="mt-3 rounded-xl border border-white/15 bg-white/[0.1] p-2.5"><p className="text-[11px] font-black text-white">Perakende POS</p><p className="text-[8px] text-white/70">Hızlı kasa</p></div>
                    )}
                    {loginLayout === "serit" && (
                      <div className="mt-3 flex flex-wrap gap-x-2 gap-y-1 rounded-lg border border-white/12 bg-white/[0.06] px-2 py-1.5">{["POS", "Depo", "E-Tic.", "Loj."].map((x) => <span key={x} className="text-[9px] font-bold text-white">{x}</span>)}</div>
                    )}
                  </div>
                  <span className="text-[10px] text-white/60">Bulut ERP · 2026</span>
                </div>
              </div>
            </div>
          </div>
          <details className="mt-5 rounded-xl border border-slate-200 p-4" data-testid="login-texts-editor">
            <summary className="mb-3 cursor-pointer text-xs font-semibold uppercase tracking-wide text-slate-500">Giriş Yazıları (düzenlemek için tıkla — ör. "POS" → "Kamyon")</summary>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm"><span className="mb-1 block font-medium text-slate-600">Başlık (1. satır)</span>
                <input data-testid="lt-title" value={loginTexts.title} onChange={(e) => setTx("title", e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green" /></label>
              <label className="text-sm"><span className="mb-1 block font-medium text-slate-600">Başlık vurgu (2. satır, turuncu)</span>
                <input data-testid="lt-title-accent" value={loginTexts.title_accent} onChange={(e) => setTx("title_accent", e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green" /></label>
            </div>
            <label className="mt-3 block text-sm"><span className="mb-1 block font-medium text-slate-600">Slogan</span>
              <input data-testid="lt-subtitle" value={loginTexts.subtitle} onChange={(e) => setTx("subtitle", e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green" /></label>
            <p className="mb-2 mt-4 text-xs font-semibold text-slate-500">Özellik kartları (Klasik / Liste düzeni)</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {(loginTexts.features || []).map((f, i) => (
                <div key={i} className="flex gap-2">
                  <input data-testid={`lt-feat-label-${i}`} value={f.label} onChange={(e) => setFeat(i, "label", e.target.value)} placeholder="Başlık" className="h-9 w-1/2 rounded-md border border-input bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-green" />
                  <input data-testid={`lt-feat-sub-${i}`} value={f.sub} onChange={(e) => setFeat(i, "sub", e.target.value)} placeholder="Alt yazı" className="h-9 w-1/2 rounded-md border border-input bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-green" />
                </div>
              ))}
            </div>
            <p className="mb-2 mt-4 text-xs font-semibold text-slate-500">İstatistikler (İstatistik düzeni)</p>
            <div className="grid gap-2 sm:grid-cols-3">
              {(loginTexts.stats || []).map((s, i) => (
                <div key={i} className="flex gap-2">
                  <input data-testid={`lt-stat-big-${i}`} value={s.big} onChange={(e) => setStat(i, "big", e.target.value)} placeholder="1.600+" className="h-9 w-1/2 rounded-md border border-input bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-green" />
                  <input data-testid={`lt-stat-label-${i}`} value={s.label} onChange={(e) => setStat(i, "label", e.target.value)} placeholder="Etiket" className="h-9 w-1/2 rounded-md border border-input bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-green" />
                </div>
              ))}
            </div>
          </details>
          <Button data-testid="logintheme-save" onClick={saveLoginTheme} disabled={saving} className="mt-4 bg-brand-green text-white hover:bg-brand-green-600">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Kaydet
          </Button>
        </Section>
      )}

      {tab === "design" && can("design.manage") && designSub === "paneldesign" && (
        <div data-testid="settings-paneldesign-panel"><DashboardDesign /></div>
      )}

      {tab === "comms" && commsSub === "sms" && (
        <div className="grid items-start gap-5 lg:grid-cols-2" data-testid="sms-grid">
          {sms && (
          <Section>
          <p className="font-heading text-lg font-bold text-slate-900">SMS Sağlayıcısı & Bildirimleri</p>
          <p className="text-sm text-slate-500">SMS platformunuzu seçin ve anahtarlarınızı girin. Anket/kampanya SMS'leri seçili sağlayıcı ile gönderilir.</p>

          <div className="mt-4">
            <Label className="mb-1.5 block">SMS Platformu</Label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="sms-provider-picker">
              {SMS_PROVIDERS.map((p) => (
                <button key={p.id} type="button" data-testid={`sms-provider-${p.id}`} onClick={() => setSms({ ...sms, provider: p.id })}
                  className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${(sms.provider || "netgsm") === p.id ? "border-brand-green bg-brand-green/10 ring-1 ring-brand-green" : "border-slate-200 hover:bg-slate-50"}`}>
                  <span className="block text-sm font-bold text-slate-800">{p.label}</span>
                  <span className="text-[11px] text-slate-400">{p.note}</span>
                </button>
              ))}
            </div>
          </div>

          <div className={`mt-4 flex items-center gap-2 rounded-lg p-3 text-sm ${sms.configured ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="sms-status">
            {sms.configured ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
            {sms.configured
              ? `${SMS_PROVIDERS.find((x) => x.id === (sms.provider || "netgsm"))?.label} yapılandırıldı — canlı SMS gönderimi aktif.`
              : `${SMS_PROVIDERS.find((x) => x.id === (sms.provider || "netgsm"))?.label} anahtarları eksik. Aşağıdaki alanları doldurup kaydedin; canlı gönderim aktifleşir.`}
          </div>

          <div className="mt-4 rounded-lg border border-slate-200 p-4">
            <p className="mb-1 text-sm font-bold text-slate-800">{SMS_PROVIDERS.find((x) => x.id === (sms.provider || "netgsm"))?.label} Anahtarları</p>
            <p className="mb-3 text-[11px] text-slate-400">{SMS_PROVIDER_HINTS[sms.provider || "netgsm"]}</p>
            <div className="grid gap-3 sm:grid-cols-3">
              {(SMS_FIELDS[sms.provider || "netgsm"] || []).map((f) => (
                <div key={f.k}>
                  <Label>{f.label} {f.secret && sms[f.setk] && <span className="text-[10px] font-semibold text-emerald-600">(kayıtlı)</span>}</Label>
                  <Input data-testid={`sms-field-${f.k}`} type={f.secret ? "password" : "text"}
                    autoComplete={f.secret ? "new-password" : "off"}
                    value={sms[f.k] || ""} onChange={(e) => setSms({ ...sms, [f.k]: e.target.value })}
                    placeholder={f.secret && sms[f.setk] ? "•••••••• (değiştirmek için yazın)" : f.ph} className="mt-1" />
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3">
              <div className="min-w-[180px] flex-1">
                <Label>Canlı Test — telefon</Label>
                <Input data-testid="sms-cred-test-phone" value={credTestPhone} onChange={(e) => setCredTestPhone(e.target.value)} placeholder="05xx xxx xx xx" className="mt-1" />
              </div>
              <Button data-testid="sms-cred-test-send" variant="outline" onClick={sendCredTest} className="border-brand-green text-brand-green hover:bg-emerald-50">
                <Send className="mr-2 h-4 w-4" /> Test SMS Gönder
              </Button>
              <p className="w-full text-[11px] text-slate-400">Önce anahtarları kaydedin; test canlı gönderim yapar.</p>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <div className={`flex items-center justify-between rounded-lg px-3 py-2.5 ring-1 ring-inset ${sms.enabled ? "bg-emerald-50 ring-emerald-200" : "bg-red-50 ring-red-200"}`}>
              <div>
                <Label className="mb-0 font-bold">SMS Sistemi (ana anahtar)</Label>
                <p className="text-[11px] text-slate-500">Kapalıyken hiçbir otomatik veya toplu SMS gönderilmez.</p>
              </div>
              <Switch data-testid="sms-master-enabled" checked={sms.enabled} onCheckedChange={(v) => setSms({ ...sms, enabled: v })} />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5">
              <Label className="mb-0">Anket teşekkür SMS'i gönder</Label>
              <Switch data-testid="sms-thanks" checked={sms.survey_thanks} onCheckedChange={(v) => setSms({ ...sms, survey_thanks: v })} />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5">
              <Label className="mb-0">Satış sonrası anket daveti SMS'i gönder</Label>
              <Switch data-testid="sms-invite" checked={sms.survey_invite} onCheckedChange={(v) => setSms({ ...sms, survey_invite: v })} />
            </div>
            <div><Label>Gönderen adı</Label><Input data-testid="sms-sender" value={sms.sender_name} onChange={(e) => setSms({ ...sms, sender_name: e.target.value })} className="mt-1" /></div>
          </div>
          <Button data-testid="sms-save" onClick={saveSms} disabled={saving} className="mt-4 bg-brand-green text-white hover:bg-brand-green-600">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Kaydet
          </Button>
        </Section>
          )}
          <Section>
          <div className="flex items-center gap-2">
            <Send className="h-5 w-5 text-brand-orange" />
            <p className="font-heading text-lg font-bold text-slate-900">Toplu SMS / Kampanya</p>
          </div>
          <p className="text-sm text-slate-500">Doğum günü kutlaması, kampanya veya duyuru SMS'i. SMS sağlayıcısı anahtarı yoksa gönderim atlanır (yapı hazır).</p>

          <div className="mt-4">
            <Label>Hedef Kitle</Label>
            <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[["all", "Tümü", counts?.all], ["wholesale", "Toptan", counts?.wholesale], ["retail", "Perakende", counts?.retail], ["birthday_today", "Bugün Doğum Günü", counts?.birthday_today]].map(([v, l, n]) => (
                <button key={v} type="button" data-testid={`camp-audience-${v}`} onClick={() => setCamp({ ...camp, audience: v })}
                  className={`rounded-md border px-3 py-2 text-left text-sm font-medium transition-colors ${camp.audience === v ? "border-brand-orange bg-brand-orange/10 text-brand-orange" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                  <span className="block">{l}</span>
                  <span className="text-[11px] text-slate-400"><Users className="mr-0.5 inline h-3 w-3" />{n ?? 0} kişi</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <Label>Mesaj</Label>
            <textarea data-testid="camp-message" value={camp.message} onChange={(e) => setCamp({ ...camp, message: e.target.value })}
              rows={3} maxLength={320} placeholder="Örn: Dogum gununuz kutlu olsun! Bugune ozel %10 indirim sizi bekliyor. - Groot Market"
              className="mt-1 w-full rounded-md border border-input bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange" />
            <p className="mt-1 text-[11px] text-slate-400">{camp.message.length}/320 karakter · Türkçe karakter kullanmayın (SMS uyumu).</p>
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[180px]">
              <Label>Test numarası (opsiyonel)</Label>
              <Input data-testid="camp-test-phone" value={camp.test_phone} onChange={(e) => setCamp({ ...camp, test_phone: e.target.value })} placeholder="05xx xxx xx xx" className="mt-1" />
            </div>
            <Button data-testid="camp-test-send" variant="outline" onClick={() => sendCampaign(true)} disabled={sending} className="border-brand-orange text-brand-orange hover:bg-orange-50">
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Test Gönder
            </Button>
            <Button data-testid="camp-send-wholesale" variant="outline" onClick={() => sendCampaign(false, "wholesale")} disabled={sending} className="border-brand-green text-brand-green hover:bg-emerald-50">
              <Tag className="mr-2 h-4 w-4" /> Toptan/VIP'e Gönder
            </Button>
            <Button data-testid="camp-send" onClick={() => sendCampaign(false)} disabled={sending} className="bg-brand-orange text-white hover:bg-orange-600">
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Kampanyayı Gönder
            </Button>
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50/60 p-3">
            <div className="min-w-[220px] flex-1">
              <Label className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-brand-green" /> Zamanla (opsiyonel)</Label>
              <Input data-testid="camp-schedule" type="datetime-local" value={campSchedule} onChange={(e) => setCampSchedule(e.target.value)} className="mt-1" />
            </div>
            <Button data-testid="camp-schedule-send" variant="outline" onClick={scheduleCampaign} disabled={sending || !campSchedule} className="border-brand-green text-brand-green hover:bg-emerald-50">
              <CalendarClock className="mr-2 h-4 w-4" /> Zamanla ve Kaydet
            </Button>
            <p className="w-full text-[11px] text-slate-400">Seçtiğiniz gün/saatte kampanya otomatik gönderilir (hedef kitle o an yeniden hesaplanır). Bekleyen kampanyayı geçmişten iptal edebilirsiniz.</p>
          </div>

          {campResult && (
            <div data-testid="camp-result" className={`mt-4 rounded-lg p-3 text-sm ${campResult.disabled ? "bg-red-50 text-red-700" : campResult.mocked ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
              {campResult.scheduled
                ? `Zamanlandı: ${campResult.scheduled_at ? new Date(campResult.scheduled_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""} · ${campResult.total} hedef. Zamanı gelince otomatik gönderilecek.`
                : campResult.disabled
                  ? "SMS sistemi kapalı. Yukarıdaki 'SMS Sistemi (ana anahtar)' açık olmalı."
                  : campResult.mocked
                    ? `MOCKED: ${campResult.total} hedef bulundu ancak SMS sağlayıcısı anahtarı olmadığı için gönderim atlandı. Ayarlar → SMS Sağlayıcısı'ndan anahtarları girin.`
                    : `Gönderildi: ${campResult.sent} başarılı · ${campResult.skipped} atlandı · ${campResult.total} hedef.`}
            </div>
          )}

          {campHistory.length > 0 && (
            <div className="mt-5" data-testid="camp-history">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Gönderim Geçmişi</p>
              <div className="max-h-64 space-y-1.5 overflow-y-auto">
                {campHistory.map((h) => (
                  <div key={h.id} data-testid="camp-history-row" className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="truncate text-slate-700">{h.message}</div>
                      <div className="text-[11px] text-slate-400">{h.at ? new Date(h.at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""} · {({ all: "Tümü", wholesale: "Toptan/VIP", retail: "Perakende", birthday_today: "Doğum günü", selected: "Seçili" })[h.audience] || h.audience} · {h.targets ?? 0} hedef · {h.by || ""}</div>
                    </div>
                    <div className="shrink-0 text-right text-[11px]">
                      {h.status === "scheduled" ? (
                        <div className="flex items-center gap-2">
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700">Zamanlandı: {h.scheduled_at ? new Date(h.scheduled_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                          <button data-testid={`camp-cancel-${h.id}`} onClick={() => cancelCampaign(h.id)} className="font-medium text-red-500 hover:underline">İptal</button>
                        </div>
                      ) : (
                        <>
                          <span className="font-semibold text-emerald-600">{h.sent ?? 0} gönderildi</span>
                          {h.skipped ? <span className="text-slate-400"> · {h.skipped} atlandı</span> : null}
                          {h.configured === false ? <div className="text-amber-600">MOCKED</div> : null}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>
        </div>
      )}

      {tab === "comms" && commsSub === "weekly" && (
        <Section>
          <div className="flex items-center gap-2">
            <Send className="h-5 w-5 text-brand-green" />
            <p className="font-heading text-lg font-bold text-slate-900">Haftalık Özet E-postası</p>
          </div>
          <p className="text-sm text-slate-500">Yöneticilere geçen 7 günün özeti (ciro, kâr, yoğun saatler, kritik stok, SKT, en çok satan) otomatik e-posta ile gönderilir. <b>Saat sabittir: 08:00 (İstanbul)</b> — yalnız günü seçebilirsiniz.</p>
          {!wkCfg ? <p className="mt-4 text-sm text-slate-400">Yükleniyor…</p> : (
            <div className="mt-4 space-y-4" data-testid="weekly-summary-panel">
              <label className="flex cursor-pointer items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5 ring-1 ring-inset ring-slate-200">
                <span className="text-sm font-bold text-slate-700">Haftalık özeti otomatik gönder</span>
                <Switch data-testid="wk-enabled" checked={wkCfg.enabled} onCheckedChange={(v) => setWkCfg({ ...wkCfg, enabled: v })} />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Gönderim Günü (saat 08:00 sabit)</Label>
                  <select data-testid="wk-weekday" value={wkCfg.weekday} onChange={(e) => setWkCfg({ ...wkCfg, weekday: Number(e.target.value) })}
                    className="mt-1 w-full rounded-md border border-input bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                    {["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"].map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Alıcılar</Label>
                  <select data-testid="wk-recipients" value={wkCfg.recipients} onChange={(e) => setWkCfg({ ...wkCfg, recipients: e.target.value })}
                    className="mt-1 w-full rounded-md border border-input bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                    <option value="admins">Tüm Yöneticiler</option>
                    <option value="bigboss">Yalnız Big BOSS</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block">İçerik Bölümleri</Label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {[["revenue", "Ciro"], ["profit", "Kâr"], ["busy_hours", "Yoğun Saatler"], ["low_stock", "Kritik Stok"], ["expiring", "SKT Yaklaşan"], ["top_sellers", "En Çok Satan"]].map(([k, l]) => (
                    <label key={k} className="flex cursor-pointer items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm">
                      <span className="text-slate-600">{l}</span>
                      <Switch data-testid={`wk-sec-${k}`} checked={!!wkCfg.sections?.[k]} onCheckedChange={(v) => setWkCfg({ ...wkCfg, sections: { ...wkCfg.sections, [k]: v } })} />
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button data-testid="wk-save" onClick={saveWkCfg} disabled={wkBusy} className="bg-brand-green text-white hover:bg-brand-green-600"><Save className="mr-1.5 h-4 w-4" /> Kaydet</Button>
                <Button data-testid="wk-send-now" variant="outline" onClick={sendWkNow} disabled={wkBusy}><Send className="mr-1.5 h-4 w-4" /> Şimdi Gönder (test)</Button>
              </div>
            </div>
          )}
        </Section>
      )}

      {tab === "comms" && commsSub === "risk" && (
        <Section>
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-brand-green" />
            <p className="font-heading text-lg font-bold text-slate-900">Günlük Risk Özeti E-postası</p>
          </div>
          <p className="text-sm text-slate-500">Amirlere önceki günün Risk Radar özeti (kasa açığı/fazlası, transfer eksik teslim, yetkisiz araç denemesi) her sabah <b>08:00 (İstanbul)</b> dijital özetiyle gönderilir. Risk yoksa gönderilmez. Anlık göndermek için <b>Şimdi Gönder</b>.</p>
          {!riskCfg ? <p className="mt-4 text-sm text-slate-400">Yükleniyor…</p> : (
            <div className="mt-4 space-y-4" data-testid="daily-risk-panel">
              <label className="flex cursor-pointer items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5 ring-1 ring-inset ring-slate-200">
                <span className="text-sm font-bold text-slate-700">Günlük risk özetini otomatik gönder</span>
                <Switch data-testid="risk-enabled" checked={riskCfg.enabled} onCheckedChange={(v) => setRiskCfg({ ...riskCfg, enabled: v })} />
              </label>
              <div>
                <Label>Alıcılar</Label>
                <select data-testid="risk-recipients" value={riskCfg.recipients} onChange={(e) => setRiskCfg({ ...riskCfg, recipients: e.target.value })}
                  className="mt-1 w-full rounded-md border border-input bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                  <option value="admins">Tüm Yöneticiler</option>
                  <option value="custom">Özel e-posta listesi</option>
                </select>
              </div>
              {riskCfg.recipients === "custom" && (
                <div>
                  <Label>Özel Alıcı E-postaları (virgülle ayırın)</Label>
                  <Input data-testid="risk-emails" value={(riskCfg.emails || []).join(", ")}
                    onChange={(e) => setRiskCfg({ ...riskCfg, emails: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    placeholder="mudur@firma.com, patron@firma.com" className="mt-1" />
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button data-testid="risk-save" onClick={saveRiskCfg} disabled={riskBusy} className="bg-brand-green text-white hover:bg-brand-green-600"><Save className="mr-1.5 h-4 w-4" /> Kaydet</Button>
                <Button data-testid="risk-send-now" variant="outline" onClick={sendRiskNow} disabled={riskBusy}><Send className="mr-1.5 h-4 w-4" /> Şimdi Gönder</Button>
              </div>
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
